import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { AssessmentResults, VersionConfig } from '@/types/assessment';
import { highSchoolConfig } from '@/data/highschool';
import { collegeConfig } from '@/data/college';
import { professionalConfig } from '@/data/professional';
import ResultsView from '@/components/assessment/ResultsView';
import { Sparkles } from 'lucide-react';

const configs: Record<string, VersionConfig> = {
  highschool: highSchoolConfig,
  college: collegeConfig,
  professional: professionalConfig,
};

const SharedResults = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [results, setResults] = useState<AssessmentResults | null>(null);
  const [config, setConfig] = useState<VersionConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchResults = async () => {
      if (!shareId) return;
      // Migration 20260427000001: direct table reads of assessment_results
      // are no longer allowed for anon callers. We call the get_shared_result
      // RPC which is SECURITY DEFINER and returns only the safe columns
      // (no selections, no open_texts, no user_id, no school_id).
      const { data, error } = await supabase
        .rpc('get_shared_result' as never, { p_share_id: shareId });

      if (error || !data) {
        setError('Path not found or link has expired.');
      } else {
        const row = data as { results: unknown; edition: string };
        setResults(row.results as AssessmentResults);
        setConfig(configs[row.edition] || highSchoolConfig);
      }
      setLoading(false);
    };
    fetchResults();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-journey-atmosphere flex items-center justify-center" role="status" aria-label="Loading blueprint">
        <Sparkles className="w-8 h-8 text-primary animate-spin" aria-hidden="true" />
      </div>
    );
  }

  if (error || !results || !config) {
    return (
      <div className="min-h-screen bg-journey-atmosphere flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center space-y-4">
          <h2 className="text-2xl font-display font-bold text-foreground">Oops!</h2>
          <p className="text-muted-foreground">{error}</p>
          <Link to="/" className="inline-block px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm">
            Take the Assessment →
          </Link>
        </motion.div>
      </div>
    );
  }

  return <ResultsView results={results} config={config} onStartOver={() => window.location.href = '/'} />;
};

export default SharedResults;
